import React from 'react';
import { Mail, Phone, MapPin, Instagram, Twitter, MessageCircle, Linkedin, FileText } from 'lucide-react';
import { contactInfo } from '../utils/mock';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import CommissionDisclosure from './CommissionDisclosure';

const Contact = () => {
  return (
    <section id="contact" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-block bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Get In Touch
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Let's Start Your Financial Journey
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Connect with our expert financial advisors to achieve your investment goals
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Email Card */}
            <Card className="border-2 border-slate-200 hover:border-amber-500 hover:shadow-xl transition-all duration-300 group">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Mail className="text-white" size={32} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Email Us</h3>
                <p className="text-slate-600 mb-4">Send us an email anytime</p>
                <a 
                  href={`mailto:${contactInfo.email}`}
                  className="text-amber-600 hover:text-amber-700 font-semibold text-sm whitespace-nowrap inline-block"
                >
                  {contactInfo.email}
                </a>
              </CardContent>
            </Card>

            {/* Phone Card */}
            <Card className="border-2 border-slate-200 hover:border-amber-500 hover:shadow-xl transition-all duration-300 group">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Phone className="text-white" size={32} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Call Us</h3>
                <p className="text-slate-600 mb-4">Mon-Sat, 9:00 AM - 6:00 PM</p>
                <a 
                  href={`tel:${contactInfo.phone}`}
                  className="text-blue-600 hover:text-blue-700 font-semibold text-2xl"
                >
                  {contactInfo.phone}
                </a>
              </CardContent>
            </Card>
          </div>

          {/* WhatsApp CTA */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-200 rounded-2xl p-8 mb-12">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-[#25D366] rounded-full flex items-center justify-center flex-shrink-0 p-3">
                  <img 
                    src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" 
                    alt="WhatsApp"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Quick Connect on WhatsApp</h3>
                  <p className="text-slate-600">Get instant responses to your investment queries</p>
                </div>
              </div>
              <Button
                onClick={() => window.open(`https://wa.me/${contactInfo.whatsapp.replace(/\s/g, '')}`, '_blank')}
                size="lg"
                className="bg-[#25D366] hover:bg-[#20BA5A] text-white px-8 py-6 text-lg whitespace-nowrap flex items-center gap-2"
              >
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" 
                  alt="WhatsApp"
                  className="w-5 h-5"
                />
                Chat on WhatsApp
              </Button>
            </div>
          </div>

          {/* Contact Detail Form CTA */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-8 mb-12">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <FileText className="text-white" size={28} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Share Your Details</h3>
                  <p className="text-slate-600">Fill our secure contact form and our advisors will reach out to you</p>
                </div>
              </div>
              <Button
                onClick={() => window.open('https://rubiconfin.investwell.app/app/#/contactUs', '_blank', 'noopener,noreferrer')}
                size="lg"
                data-testid="contact-detail-form-btn"
                className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white px-8 py-6 text-lg whitespace-nowrap flex items-center gap-2 shadow-lg"
              >
                <FileText size={20} />
                Contact Detail Form
              </Button>
            </div>
          </div>

          {/* Location & Social */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* Office Location */}
            <Card className="border-2 border-slate-200">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-slate-600" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-lg mb-2">Visit Our Office</h4>
                    <p className="text-slate-600 leading-relaxed">
                      {contactInfo.address}
                    </p>
                  </div>
                </div>
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-sm text-slate-500 mb-2 font-semibold">Business Hours:</p>
                  <p className="text-slate-600">Monday - Saturday: 9:00 AM - 6:00 PM</p>
                  <p className="text-slate-600">Sunday: Closed</p>
                </div>
              </CardContent>
            </Card>

            {/* Social Media */}
            <Card className="border-2 border-slate-200">
              <CardContent className="p-8">
                <h4 className="font-bold text-slate-900 text-lg mb-6">Follow Us</h4>
                <div className="space-y-4">
                  <a
                    href={`https://instagram.com/${contactInfo.instagram.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 rounded-xl hover:shadow-lg transition-all group"
                    style={{ background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)' }}
                  >
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform p-2">
                      <img 
                        src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Instagram_icon.png" 
                        alt="Instagram"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div>
                      <p className="font-semibold text-white">Instagram</p>
                      <p className="text-white/90 text-sm">{contactInfo.instagram}</p>
                    </div>
                  </a>

                  <a
                    href={`https://twitter.com/${contactInfo.twitter.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 bg-black rounded-xl hover:shadow-lg transition-all group"
                  >
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform p-2">
                      <img 
                        src="https://about.x.com/content/dam/about-twitter/x/brand-toolkit/logo-black.png.twimg.1920.png" 
                        alt="X"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div>
                      <p className="font-semibold text-white">X (Twitter)</p>
                      <p className="text-white/80 text-sm">{contactInfo.twitter}</p>
                    </div>
                  </a>

                  <a
                    href={`https://${contactInfo.linkedin}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 bg-[#0A66C2] rounded-xl hover:shadow-lg transition-all group"
                  >
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform p-2">
                      <img 
                        src="https://upload.wikimedia.org/wikipedia/commons/c/ca/LinkedIn_logo_initials.png" 
                        alt="LinkedIn"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div>
                      <p className="font-semibold text-white">LinkedIn</p>
                      <p className="text-white/90 text-sm">Rohit Jain Bhansali</p>
                    </div>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Registration Details */}
          <div className="mt-12 text-center bg-slate-50 rounded-2xl p-8 border-2 border-slate-200">
            <p className="text-slate-600 text-sm mb-4 font-semibold">Certified & Registered</p>
            <div className="flex flex-wrap justify-center gap-4 mb-6">
              <span className="bg-white px-6 py-3 rounded-lg border-2 border-slate-200 font-semibold text-slate-900">
                ARN: 102867
              </span>
              <span className="bg-white px-6 py-3 rounded-lg border-2 border-slate-200 font-semibold text-slate-900">
                APMI: APMI-APRN01145
              </span>
            </div>
            
            {/* MSME Registration */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border-2 border-green-200 mb-6">
              <p className="text-green-900 text-xs font-semibold mb-1">
                🏛️ MSME Registered Enterprise
              </p>
              <p className="text-green-800 text-xs mb-2">
                Ministry of Micro, Small and Medium Enterprises, Government of India
              </p>
              <p className="text-green-900 font-bold text-sm">
                UDYAM: UDYAM-WB-14-0192124
              </p>
            </div>
            {/* Investor Grievances */}
            <div className="mt-6 pt-6 border-t-2 border-slate-200">
              <p className="text-slate-700 text-sm font-semibold mb-3">For Investor Grievances</p>
              <a 
                href="mailto:complaints@rubiconfinservices.com"
                className="inline-flex items-center gap-2 bg-red-50 hover:bg-red-100 border-2 border-red-200 text-red-700 px-6 py-3 rounded-lg font-semibold transition-all hover:shadow-md"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="20" height="16" x="2" y="4" rx="2"/>
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                </svg>
                complaints@rubiconfinservices.com
              </a>
            </div>

            {/* Commission Disclosure */}
            <div className="mt-6 pt-6 border-t-2 border-slate-200">
              <p className="text-slate-700 text-sm font-semibold mb-3">Transparency & Disclosure</p>
              <CommissionDisclosure />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
